import { Icon } from '@viaja/design-system';
import { type ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Folha inferior modal (seletores de aeroporto e datas, formulários curtos).
 * Fecha com Esc, toque fora ou o botão; devolve o foco a quem abriu.
 */
export function Sheet({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const first = panel.current?.querySelector<HTMLElement>(
      '[data-autofocus], input, button:not([data-close])',
    );
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab' && panel.current) {
        const items = [
          ...panel.current.querySelectorAll<HTMLElement>('button, input, select, textarea, a[href]'),
        ].filter((el) => !el.hasAttribute('disabled'));
        const firstEl = items[0];
        const lastEl = items[items.length - 1];
        if (!firstEl || !lastEl) return;
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    // biome-ignore lint/a11y/useKeyWithClickEvents: Esc já fecha pelo listener de teclado.
    <div className="app-sheet-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      {/* biome-ignore lint/a11y/useSemanticElements: <dialog> nativo muda foco e camadas; controle manual aqui. */}
      <div ref={panel} className="app-sheet" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <span className="app-sheet-grip" aria-hidden="true" />
        <div className="app-sheet-head">
          <h2 id={titleId} className="display-section">
            {title}
          </h2>
          <button type="button" className="app-iconbtn" aria-label="Fechar" data-close onClick={onClose}>
            <Icon name="fechar" size={24} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
